import React, { useEffect, useState } from "react";
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from "lucide-react";

export type ToastType = "success" | "error" | "info" | "warning";

export interface ToastMessage {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

interface ToastProps {
  toast: ToastMessage;
  onClose: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ toast, onClose }) => {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const duration = toast.duration || 3000;
    const timer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [toast.id]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose(toast.id);
    }, 300); // Match animation duration
  };

  const getToastStyles = () => {
    switch (toast.type) {
      case "success":
        return {
          bg: "bg-white",
          border: "border-[#0DB5B5]",
          icon: <CheckCircle className="w-5 h-5 text-[#0DB5B5]" />,
          iconBg: "bg-[#E5F8F8]",
        };
      case "error":
        return {
          bg: "bg-white",
          border: "border-[#FF3B30]",
          icon: <AlertCircle className="w-5 h-5 text-[#FF3B30]" />,
          iconBg: "bg-[#FFF5F5]",
        };
      case "warning":
        return {
          bg: "bg-white",
          border: "border-[#FF9500]",
          icon: <AlertTriangle className="w-5 h-5 text-[#FF9500]" />,
          iconBg: "bg-[#FFF4E5]",
        };
      case "info":
        return {
          bg: "bg-white",
          border: "border-[#007AFF]",
          icon: <Info className="w-5 h-5 text-[#007AFF]" />,
          iconBg: "bg-[#E5F2FF]",
        };
      default:
        return {
          bg: "bg-white",
          border: "border-[#E0E0E0]",
          icon: <Info className="w-5 h-5 text-[#646464]" />,
          iconBg: "bg-[#F5F5F5]",
        };
    }
  };

  const styles = getToastStyles();

  return (
    <div
      className={`${styles.bg} ${styles.border} border-l-4 rounded-md shadow-lg p-4 mb-3 min-w-[320px] max-w-md transition-all duration-300 ${
        isExiting
          ? "opacity-0 translate-x-full"
          : "opacity-100 translate-x-0"
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`${styles.iconBg} rounded-full p-1.5 flex-shrink-0`}>
          {styles.icon}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-[#1F1F1F] font-inter mb-0.5">
            {toast.title}
          </h4>
          {toast.message && (
            <p className="text-sm text-[#646464] font-inter">
              {toast.message}
            </p>
          )}
        </div>

        {/* Close button */}
        <button
          onClick={handleClose}
          className="text-[#8E8E93] hover:text-[#1F1F1F] transition-colors flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

interface ToastContainerProps {
  toasts: ToastMessage[];
  onClose: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({
  toasts,
  onClose,
}) => {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col items-end">
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onClose={onClose} />
      ))}
    </div>
  );
};

// Hook for managing toasts
export const useToast = () => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const showToast = (
    type: ToastType,
    title: string,
    message?: string,
    duration?: number
  ) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    const newToast: ToastMessage = {
      id,
      type,
      title,
      message,
      duration: duration || 3000,
    };

    setToasts((prev) => [...prev, newToast]);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const success = (title: string, message?: string, duration?: number) => {
    showToast("success", title, message, duration);
  };

  const error = (title: string, message?: string, duration?: number) => {
    showToast("error", title, message, duration);
  };

  const warning = (title: string, message?: string, duration?: number) => {
    showToast("warning", title, message, duration);
  };

  const info = (title: string, message?: string, duration?: number) => {
    showToast("info", title, message, duration);
  };

  return {
    toasts,
    removeToast,
    success,
    error,
    warning,
    info,
  };
};

export default Toast;